import type { CSSProperties } from 'react';
import { useId } from 'react';
import { makeStyles, tokens, Body1Strong } from '@fluentui/react-components';

type OttoLogoProps = {
  size?: number;
  className?: string;
  style?: CSSProperties;
  label?: string;
};

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Otto logo — Workflow Automation branded icon.
 *
 * The icon uses multi-stop gradients across four quadrants
 * (blue, purple, teal).
 */
export const OttoLogo = ({ size = 20, className, style, label }: OttoLogoProps) => {
  const styles = useStyles();
  const id = useId();
  // When the logo carries a visible text label, the SVG is decorative
  // and screen readers should announce only the text. When there's no
  // visible label, expose the product name as the SVG's accessible name.
  const svgLabel = label ? '' : 'Logic Apps Automation logo';

  const svg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 96 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...(svgLabel
        ? { role: 'img', 'aria-label': svgLabel }
        : { role: 'presentation', 'aria-hidden': true })}
    >
      <defs>
        <linearGradient id={`${id}-g0`} x1="47.9903" y1="28.9601" x2="96.5558" y2="28.9601" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0094F0" />
          <stop offset="1" stopColor="#0067BF" />
        </linearGradient>
        <linearGradient id={`${id}-g1`} x1="79.882" y1="21.9697" x2="67.8622" y2="7.35587" gradientUnits="userSpaceOnUse">
          <stop stopColor="#003580" />
          <stop offset="0.55" stopColor="#0081DB" stopOpacity="0.31188" />
          <stop offset="1" stopColor="#0081DB" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-g2`} x1="45.9924" y1="27.7961" x2="5.10079" y2="64.7668" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0094F0" />
          <stop offset="0.511991" stopColor="#4894FE" />
          <stop offset="1" stopColor="#8F8FFF" />
        </linearGradient>
        <linearGradient id={`${id}-g3`} x1="23.1803" y1="72.4876" x2="7.11923" y2="54.5039" gradientUnits="userSpaceOnUse">
          <stop offset="0.102325" stopColor="#E4A7FE" />
          <stop offset="1" stopColor="#4894FE" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-g4`} x1="-0.575185" y1="81.0207" x2="45.2865" y2="101.93" gradientUnits="userSpaceOnUse">
          <stop stopColor="#5B2AB5" />
          <stop offset="0.64356" stopColor="#8B52F4" />
        </linearGradient>
        <linearGradient id={`${id}-g5`} x1="15.2086" y1="75.029" x2="23.0474" y2="84.5595" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4B20A0" />
          <stop offset="0.998551" stopColor="#8B52F4" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-g6`} x1="95.9895" y1="20.0365" x2="45.7625" y2="60.5936" gradientUnits="userSpaceOnUse">
          <stop stopColor="#66C0FF" />
          <stop offset="0.552928" stopColor="#7B7BFF" />
          <stop offset="1" stopColor="#8B52F4" />
        </linearGradient>
        <linearGradient id={`${id}-g7`} x1="75.9905" y1="20.5331" x2="90.4854" y2="35.9058" gradientUnits="userSpaceOnUse">
          <stop stopColor="#B3E0FF" />
          <stop offset="0.639201" stopColor="#9C6CFE" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <rect width="96" height="96" fill="white" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        {/* Top-right quadrant */}
        <path d="M71.3 29L85.2 17.68L89.6 14.08L93.56 10.84L93.78 10.66C95.14 9.56 96 7.88 96 6C96 2.68 93.32 0 90 0H48V29H71.3Z" fill={`url(#${id}-g0)`} />
        <path d="M71.3 29L85.2 17.68L89.6 14.08L93.56 10.84L93.78 10.66C95.14 9.56 96 7.88 96 6C96 2.68 93.32 0 90 0H48V29H71.3Z" fill={`url(#${id}-g1)`} fillOpacity="0.5" />
        {/* Left arc */}
        <path d="M10.8 78.32L24.7 67L25.86 66.06C29.6 63 31.14 58 29.78 53.38C29.28 51.66 29 49.86 29 48C29 37.5 37.5 29 48 29V0C21.5 0 0 21.5 0 48C0 55.394 1.75 63.1 5.16 69.66C5.26 69.88 5.36 70.08 5.48 70.28C8.12 75.32 6.86 81.54 2.44 85.16L6.4 81.92L10.8 78.32Z" fill={`url(#${id}-g2)`} />
        <path d="M10.8 78.32L24.7 67L25.86 66.06C29.6 63 31.14 58 29.78 53.38C29.28 51.66 29 49.86 29 48C29 37.5 37.5 29 48 29V0C21.5 0 0 21.5 0 48C0 55.394 1.75 63.1 5.16 69.66C5.26 69.88 5.36 70.08 5.48 70.28C8.12 75.32 6.86 81.54 2.44 85.16L6.4 81.92L10.8 78.32Z" fill={`url(#${id}-g3)`} fillOpacity="0.8" />
        {/* Bottom-left quadrant */}
        <path d="M24.7 67L10.8 78.32L6.4 81.92L2.44 85.16L2.22 85.34C0.86 86.44 0 88.12 0 90C0 93.32 2.68 96 6 96H48V67H24.7Z" fill={`url(#${id}-g4)`} />
        <path d="M24.7 67L10.8 78.32L6.4 81.92L2.44 85.16L2.22 85.34C0.86 86.44 0 88.12 0 90C0 93.32 2.68 96 6 96H48V67H24.7Z" fill={`url(#${id}-g5)`} fillOpacity="0.5" />
        {/* Right arc */}
        <path d="M90.52 25.72C87.88 20.66 89.14 14.46 93.56 10.84L89.6 14.08L85.2 17.68L71.3 29L70.14 29.94C66.4 33 64.86 38 66.22 42.62C66.72 44.34 67 46.14 67 48C67 58.5 58.5 67 48 67V96C74.5 96 96 74.5 96 48C96 40.392 94.056 32.524 90.84 26.34C90.74 26.12 90.64 25.92 90.52 25.72Z" fill={`url(#${id}-g6)`} />
        <path d="M90.52 25.72C87.88 20.66 89.14 14.46 93.56 10.84L89.6 14.08L85.2 17.68L71.3 29L70.14 29.94C66.4 33 64.86 38 66.22 42.62C66.72 44.34 67 46.14 67 48C67 58.5 58.5 67 48 67V96C74.5 96 96 74.5 96 48C96 40.392 94.056 32.524 90.84 26.34C90.74 26.12 90.64 25.92 90.52 25.72Z" fill={`url(#${id}-g7)`} fillOpacity="0.8" />
      </g>
    </svg>
  );

  if (!label) {
    return (
      <span className={className} style={{ display: 'inline-flex', width: size, height: size, flexShrink: 0, ...style }}>
        {svg}
      </span>
    );
  }

  return (
    <span className={`${styles.root}${className ? ` ${className}` : ''}`} style={style}>
      {svg}
      <Body1Strong className={styles.label}>{label}</Body1Strong>
    </span>
  );
};
